import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adjustAccounts, getAccountsList } from "./accounts_halper";
import { Backdrop, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { getUserAccess } from "../frames/common";
import WarningConfirm from "../frames/waring_confirm";
import MenuFrame from "../frames/menu_frame";

function Accounts() {
    const [isLoading, setIsLoading] = useState(false);
    const [accList, setAccList] = useState(null);
    const [allAccList, setAllAccList] = useState(null);
    const [finAccess, setFinAccess] = useState(false);
    const [adjustment, setAdjustment] = useState(false);
    const navigator = useNavigate();

    const initData = async (auth) => {
        if (!auth) return;
        const accs = await getAccountsList();
        setAccList(accs);
        setAllAccList(accs);
        setFinAccess(await getUserAccess("ACCOUNTS_FIN"));
    };

    const handleSearch = (event) => {
        if (allAccList === null) return;

        const value = event.target.value.toLowerCase();

        const newList = allAccList.filter(
            (itm) =>
                itm.name.toLowerCase().startsWith(value) ||
                itm.ac_type.toLowerCase().startsWith(value)
        );

        if (newList.length === 0) setAccList(allAccList);
        else setAccList(newList);
    };

    const handleAccountAdjust = () => {
        setIsLoading(true);
        adjustAccounts()
            .then(() => getAccountsList())
            .then((res) => {
                setAccList(res);
                setIsLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setIsLoading(false);
            });
    };

    return (
        <MenuFrame permission="ACCOUNTS" screen="ACCOUNTS" onInit={initData} onSearch={handleSearch}>
            {accList === null && <LinearProgress />}
            <WarningConfirm
                open={adjustment}
                title="Critical Audit Warning"
                text="This action will execute audit on all accounts and update opening balances. This action cannot be reversed."
                onSuccess={() => {
                    setAdjustment(false);
                    handleAccountAdjust();
                }}
                onClose={() => setAdjustment(false)}
                success_text="Execute Audit"
                color="error"
            />
            <Backdrop open={isLoading} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 10, backdropFilter: "blur(4px)" })}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Card elevation={0} sx={{ mt: 2, mb: 3, borderRadius: 4, border: "1px solid", borderColor: "divider", background: "linear-gradient(135deg,#1976d2 0%,#1565c0 45%,#0d47a1 100%)", color: "white", overflow: "hidden" }}>
                <CardContent sx={{ p: 4 }}>
                    <Stack direction={{ xs: "column", md: "row" }} sx={{ justifyContent: "space-between" }} spacing={3}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>Accounts</Typography>
                            <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }} >
                                Manage accounts, transactions and financial records from one place.
                            </Typography>
                            <Chip
                                icon={<AccountBalanceWalletRoundedIcon />}
                                label={`${accList?.length || 0} Accounts`}
                                sx={{ mt: 2, bgcolor: alpha("#fff", 0.18), color: "white", fontWeight: 600, "& .MuiChip-icon": { color: "white" } }}
                            />
                        </Box>
                        <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ direction: "row", flexWrap: "wrap", gap: 1.5, alignItems: "center", justifyContent: "flex-end" }}>
                            <Button
                                startIcon={<AddRoundedIcon />}
                                variant="contained"
                                onClick={() => navigator("/accounts/new")}
                                sx={{ bgcolor: "white", color: "primary.main", fontWeight: 700, borderRadius: 3, "&:hover": { bgcolor: "grey.100" } }}
                            >
                                New Account
                            </Button>
                            <Button
                                startIcon={<ReceiptLongRoundedIcon />}
                                variant="outlined"
                                onClick={() => navigator("/accounts/transaction")}
                                sx={{ color: "white", borderColor: alpha("#fff", 0.45), borderRadius: 3, "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.08) } }}
                            >
                                Transactions
                            </Button>

                            {finAccess && (
                                <>
                                    <Button
                                        startIcon={<ManageSearchRoundedIcon />}
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setAdjustment(true)}
                                        sx={{ borderRadius: 3, bgcolor: alpha("#fff", 0.08) }}
                                    >
                                        Audit
                                    </Button>

                                    <Button
                                        startIcon={<PaymentsRoundedIcon />}
                                        variant="outlined"
                                        onClick={() => navigator("cash-note")}
                                        sx={{ color: "white", borderColor: alpha("#fff", 0.45), borderRadius: 3, "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.08) } }}
                                    >
                                        Cash Note
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", border: "1px solid", borderColor: "divider" }} >
                <Box sx={{ px: 3, py: 2.25 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Account List</Typography>
                    <Typography variant="body2" color="textSecondary">Click any account to view its transaction history.</Typography>
                </Box>

                <Divider />

                <TableContainer>
                    <Table size="small" sx={{ tableLayout: "fixed", "& .MuiTableCell-root": { py: 0.75, px: 1.5, fontSize: "0.82rem", borderColor: "divider", whiteSpace: "nowrap", }, }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.100", "& .MuiTableCell-root": { py: 1, fontWeight: 700, fontSize: "0.75rem", letterSpacing: 0.4, color: "textSecondary", }, }}>
                                <TableCell width={260}>Account</TableCell>
                                <TableCell width={170}>Type</TableCell>
                                <TableCell align="right" width={140}>Balance</TableCell>
                                <TableCell align="right" width={150}>Opening</TableCell>
                                <TableCell width={130}>Opening Date</TableCell>
                                <TableCell width={40} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {accList?.map((row, idx) => (
                                <TableRow key={idx} hover onClick={() => navigator("/accounts/transaction/" + row.ac_id)} sx={{ cursor: "pointer", height: 42, transition: "0.15s", "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04), }, "& .MuiTableCell-root": { py: 0.6, }, }} >
                                    <TableCell>
                                        <Stack spacing={0}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                {row.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.1 }}>
                                                #{row.ac_id}
                                            </Typography>
                                        </Stack>
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={`${row.ac_type} • ${row.scheme === "debit" ? "D" : "C"}`}
                                            color={row.scheme === "debit" ? "success" : "warning"}
                                            variant="outlined"
                                            sx={{ height: 22, fontSize: "0.7rem", "& .MuiChip-label": { px: 1, }, }}
                                        />
                                    </TableCell>

                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600 }} color={row.balance < 0 ? "error" : "success"}>
                                            {row.balance < 0 ? "-" : ""} ₹{Math.abs(row.balance).toFixed(2)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {row.opening_balance < 0 ? "-" : ""} ₹{Math.abs(row.opening_balance).toFixed(2)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                            {row.opening_date}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} color="action" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MenuFrame>
    );
}

export default Accounts;
